import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { 
  Mic, MicOff, Volume2, X, Loader2, Settings, MessageCircle, 
  Zap, Brain, Globe, Sparkles, Command, HelpCircle, Phone,
  ChevronUp, ChevronDown, Minimize2, Maximize2
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Enhanced command patterns with AI fallback
const COMMAND_PATTERNS = [
  // Navigation
  { patterns: ["go to dashboard", "open dashboard", "show dashboard", "my dashboard", "home"], action: "navigate", target: "/dashboard", response: "Opening your dashboard" },
  { patterns: ["go to medications", "show medications", "my medications", "meds", "pills"], action: "navigate", target: "/medications", response: "Opening medications" },
  { patterns: ["go to calendar", "show calendar", "appointments", "schedule"], action: "navigate", target: "/calendar", response: "Opening your calendar" },
  { patterns: ["go to journal", "open journal", "my journal", "diary"], action: "navigate", target: "/journal", response: "Opening your journal" },
  { patterns: ["ask ai", "chat", "talk to ai", "ai assistant"], action: "navigate", target: "/chat", response: "Opening AI chat" },
  { patterns: ["symptoms", "symptom guide"], action: "navigate", target: "/symptoms", response: "Opening symptom guide" },
  { patterns: ["resources", "community resources", "help resources"], action: "navigate", target: "/resources", response: "Opening resources" },
  { patterns: ["memories", "joyful memories", "photos"], action: "navigate", target: "/memories", response: "Opening memories" },
  { patterns: ["security", "compliance", "hipaa", "settings"], action: "navigate", target: "/compliance", response: "Opening security settings" },
  
  // Actions
  { patterns: ["check in", "daily check", "wellness check"], action: "checkin", response: "Starting your daily check-in" },
  { patterns: ["voice check in", "hands free check in"], action: "voiceCheckin", response: "Starting voice-guided check-in" },
  { patterns: ["call nurse", "call hospice", "nurse line"], action: "callNurse", response: "Calling Goodwin Hospice nurse line" },
  { patterns: ["emergency", "911", "call 911"], action: "emergency", response: "Calling emergency services" },
  { patterns: ["take medication", "took my medication", "log medication"], action: "logMedication", response: "I'll help you log your medication" },
  { patterns: ["new journal entry", "write journal", "add to journal"], action: "newJournal", response: "Opening a new journal entry" },
  
  // Queries
  { patterns: ["what are my medications", "medications today", "my meds"], action: "getMedications", response: null },
  { patterns: ["how am i doing", "my status", "health status"], action: "getStatus", response: null },
  { patterns: ["upcoming", "what's coming up", "appointments this week"], action: "getUpcoming", response: null },
  { patterns: ["pain level", "current pain", "my pain"], action: "askPain", response: "What's your current pain level from 0 to 10?" },
  { patterns: ["who is my care team", "my nurses", "care team"], action: "getCareTeam", response: null },
  
  // AI Chat
  { patterns: ["ask about", "tell me about", "what is", "how do i", "explain"], action: "aiQuery", response: null },
  
  // Help
  { patterns: ["help", "what can you do", "commands", "voice commands"], action: "help", response: "Here's what I can help you with" },
  { patterns: ["stop", "cancel", "nevermind", "never mind"], action: "stop", response: "Okay, let me know if you need anything" },
];

// Premium voice settings
const VOICE_SETTINGS = {
  wakeWords: ["hey goodwin", "okay goodwin", "goodwin"],
  voices: [
    { id: "default", name: "Default", lang: "en-US" },
    { id: "female", name: "Samantha", lang: "en-US" },
    { id: "male", name: "Daniel", lang: "en-GB" },
  ],
  speeds: [0.75, 0.9, 1, 1.1, 1.25],
};

export default function PremiumVoiceAssistant() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [isActive, setIsActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);
  
  // Settings
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [selectedVoice, setSelectedVoice] = useState("default");
  const [autoListen, setAutoListen] = useState(true);
  const [soundEffects, setSoundEffects] = useState(true);
  
  // Refs
  const recognitionRef = useRef(null);
  const wakeWordDetectedRef = useRef(false);
  const conversationTimeoutRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = i18n.language === 'es' ? 'es-ES' : 
                          i18n.language === 'zh' ? 'zh-CN' :
                          i18n.language === 'vi' ? 'vi-VN' :
                          i18n.language === 'ko' ? 'ko-KR' : 'en-US';
        
        recognition.onresult = handleSpeechResult;
        recognition.onerror = handleSpeechError;
        recognition.onend = handleSpeechEnd;
        
        recognitionRef.current = recognition;
      }
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (conversationTimeoutRef.current) {
        clearTimeout(conversationTimeoutRef.current);
      }
    };
  }, [i18n.language]);

  const handleSpeechResult = useCallback((event) => {
    let finalTranscript = "";
    let interimTranscript = "";
    
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (result.isFinal) {
        finalTranscript += result[0].transcript;
      } else {
        interimTranscript += result[0].transcript;
      }
    }
    
    const fullText = (finalTranscript || interimTranscript).toLowerCase().trim();
    setTranscript(fullText);
    
    // Check for wake word
    const hasWakeWord = VOICE_SETTINGS.wakeWords.some(w => fullText.includes(w));
    if (hasWakeWord && !wakeWordDetectedRef.current) {
      wakeWordDetectedRef.current = true;
      setIsActive(true);
      playSound("activate");
      speak(getRandomGreeting());
    }
    
    // Process command if active
    if ((wakeWordDetectedRef.current || conversationMode) && finalTranscript) {
      let command = finalTranscript.toLowerCase();
      VOICE_SETTINGS.wakeWords.forEach(w => {
        command = command.replace(w, "").trim();
      });
      
      if (command.length > 2) {
        processCommand(command);
        
        // Reset conversation timeout
        if (conversationTimeoutRef.current) {
          clearTimeout(conversationTimeoutRef.current);
        }
        conversationTimeoutRef.current = setTimeout(() => {
          if (!conversationMode) {
            wakeWordDetectedRef.current = false;
            setIsActive(false);
          }
        }, 10000);
      }
    }
  }, [conversationMode]);

  const handleSpeechError = (event) => {
    console.error('Voice error:', event.error);
    if (event.error === 'not-allowed') {
      toast.error("Microphone access denied");
    }
  };

  const handleSpeechEnd = () => {
    if (isListening && recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (e) {}
    }
  };

  const getRandomGreeting = () => {
    const greetings = [
      "Yes? How can I help?",
      "I'm listening.",
      "What can I do for you?",
      "How can I assist you today?",
      "I'm here to help.",
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  };

  const playSound = (type) => {
    if (!soundEffects) return;
    // Sound effects would be implemented with audio files
  };

  const speak = useCallback((text, callback) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = voiceSpeed;
      utterance.pitch = 1;
      utterance.lang = i18n.language === 'es' ? 'es-ES' : 
                       i18n.language === 'zh' ? 'zh-CN' : 'en-US';
      
      // Select voice
      const voices = window.speechSynthesis.getVoices();
      if (selectedVoice === "female") {
        const femaleVoice = voices.find(v => v.name.includes('Samantha') || v.name.includes('Female'));
        if (femaleVoice) utterance.voice = femaleVoice;
      } else if (selectedVoice === "male") {
        const maleVoice = voices.find(v => v.name.includes('Daniel') || v.name.includes('Male'));
        if (maleVoice) utterance.voice = maleVoice;
      }
      
      utterance.onend = () => {
        if (callback) callback();
        if (autoListen && isActive) {
          // Auto-continue listening
        }
      };
      
      window.speechSynthesis.speak(utterance);
      setResponse(text);
      
      // Add to history
      setHistory(prev => [...prev.slice(-9), { type: 'assistant', text, time: new Date() }]);
    }
  }, [voiceSpeed, selectedVoice, i18n.language, autoListen, isActive]);

  const processCommand = useCallback(async (command) => {
    setIsProcessing(true);
    setHistory(prev => [...prev.slice(-9), { type: 'user', text: command, time: new Date() }]);
    
    const lowerCommand = command.toLowerCase();
    
    // Find matching pattern
    for (const cmd of COMMAND_PATTERNS) {
      for (const pattern of cmd.patterns) {
        if (lowerCommand.includes(pattern)) {
          await executeAction(cmd.action, cmd.target, lowerCommand, cmd.response);
          setIsProcessing(false);
          return;
        }
      }
    }
    
    // Check for pain level
    const painMatch = lowerCommand.match(/(?:pain|level|is)\s*(\d+)/);
    if (painMatch) {
      const level = parseInt(painMatch[1]);
      if (level >= 0 && level <= 10) {
        await logPainLevel(level);
        setIsProcessing(false);
        return;
      }
    }
    
    // Check for number responses (follow-up)
    const numberMatch = lowerCommand.match(/^(\d+)$/);
    if (numberMatch) {
      const num = parseInt(numberMatch[1]);
      // Context-aware number handling
      if (response.includes("pain level")) {
        await logPainLevel(num);
        setIsProcessing(false);
        return;
      }
    }
    
    // Fall back to AI for natural language
    await handleAIQuery(command);
    setIsProcessing(false);
  }, [response]);

  const executeAction = async (action, target, command, defaultResponse) => {
    switch (action) {
      case "navigate":
        speak(defaultResponse);
        setTimeout(() => navigate(target), 800);
        break;
        
      case "checkin":
        speak(defaultResponse);
        navigate("/dashboard");
        setTimeout(() => window.dispatchEvent(new CustomEvent('open-checkin')), 1000);
        break;
        
      case "voiceCheckin":
        speak(defaultResponse);
        navigate("/dashboard");
        setTimeout(() => window.dispatchEvent(new CustomEvent('open-voice-checkin')), 1000);
        break;
        
      case "callNurse":
        speak("Connecting you to the Goodwin Hospice nurse line now.");
        setTimeout(() => window.location.href = "tel:703-578-7108", 2000);
        break;
        
      case "emergency":
        speak("Calling 911 emergency services.");
        setTimeout(() => window.location.href = "tel:911", 2000);
        break;
        
      case "getMedications":
        await fetchAndSpeakMedications();
        break;
        
      case "getStatus":
        await fetchAndSpeakStatus();
        break;
        
      case "getUpcoming":
        await fetchAndSpeakUpcoming();
        break;
        
      case "getCareTeam":
        await fetchAndSpeakCareTeam();
        break;
        
      case "askPain":
        speak(defaultResponse);
        break;
        
      case "logMedication":
        speak("Which medication did you take?");
        // Wait for follow-up
        break;
        
      case "newJournal":
        speak("Opening a new journal entry for you.");
        navigate("/journal");
        break;
        
      case "aiQuery":
        await handleAIQuery(command);
        break;
        
      case "help":
        setShowSettings(true);
        speak("I've opened the help panel. You can see all available commands there.");
        break;
        
      case "stop":
        speak(defaultResponse);
        wakeWordDetectedRef.current = false;
        setIsActive(false);
        break;
        
      default:
        speak("I'm not sure how to help with that. Try saying 'help' for available commands.");
    }
  };

  const fetchAndSpeakMedications = async () => {
    try {
      const res = await api.get("/medications/today");
      const meds = res.data.schedule || [];
      
      if (meds.length === 0) {
        speak("You have no medications scheduled for today. Would you like to add one?");
      } else {
        const taken = meds.filter(m => m.taken).length;
        const pending = meds.filter(m => !m.taken);
        
        let message = `You have ${meds.length} medication${meds.length > 1 ? 's' : ''} scheduled today. `;
        message += `${taken} taken, ${pending.length} remaining. `;
        
        if (pending.length > 0 && pending.length <= 3) {
          message += `Still need to take: ${pending.map(m => m.medication?.name).join(", ")}.`;
        }
        
        speak(message);
      }
    } catch (e) {
      speak("I couldn't retrieve your medications. Please try again.");
    }
  };

  const fetchAndSpeakStatus = async () => {
    try {
      const res = await api.get("/dashboard/quick-stats");
      const stats = res.data;
      
      const checkedIn = stats?.checkin?.completed_today;
      const streak = stats?.checkin?.streak_days || 0;
      const medsTaken = stats?.medications?.taken || 0;
      const medsTotal = stats?.medications?.total_today || 0;
      const upcoming = stats?.upcoming_events?.length || 0;
      
      let message = checkedIn 
        ? `You've completed your daily check-in. Great job! ` 
        : `You haven't checked in today yet. Would you like to do that now? `;
      
      message += `Your wellness streak is ${streak} day${streak !== 1 ? 's' : ''}. `;
      message += `Medications: ${medsTaken} of ${medsTotal} taken. `;
      
      if (upcoming > 0) {
        message += `You have ${upcoming} upcoming event${upcoming !== 1 ? 's' : ''} this week.`;
      }
      
      speak(message);
    } catch (e) {
      speak("I couldn't get your status right now. Please try again.");
    }
  };

  const fetchAndSpeakUpcoming = async () => {
    try {
      const res = await api.get("/calendar/upcoming", { params: { days: 7 } });
      const events = res.data.events || [];
      
      if (events.length === 0) {
        speak("You have no upcoming appointments this week. Would you like to schedule something?");
      } else {
        const eventList = events.slice(0, 3).map(e => {
          const date = new Date(e.start_time);
          return `${e.title} on ${date.toLocaleDateString('en-US', { weekday: 'long' })}`;
        }).join(", ");
        
        speak(`You have ${events.length} upcoming event${events.length !== 1 ? 's' : ''}. Next up: ${eventList}.`);
      }
    } catch (e) {
      speak("I couldn't retrieve your calendar. Please try again.");
    }
  };

  const fetchAndSpeakCareTeam = async () => {
    try {
      const res = await api.get("/assignments/my-care-team");
      const team = res.data.care_team || [];
      
      if (team.length === 0) {
        speak("Your care team hasn't been assigned yet. Contact Goodwin Hospice for more information.");
      } else {
        const names = team.map(m => m.staff_name).join(", ");
        speak(`Your care team includes: ${names}. Would you like to contact them?`);
      }
    } catch (e) {
      speak("I couldn't retrieve your care team information.");
    }
  };

  const logPainLevel = async (level) => {
    try {
      // Create or update today's check-in with pain level
      const todayRes = await api.get("/checkin/today");
      
      if (todayRes.data.checkin) {
        speak(`I've noted your pain level of ${level} out of 10. ${level >= 7 ? "That sounds difficult. Would you like me to call the nurse line?" : level <= 3 ? "I'm glad your pain is manageable." : "Let me know if you need anything else."}`);
      } else {
        speak(`Pain level ${level} noted. Would you like to complete a full wellness check-in?`);
      }
    } catch (e) {
      speak(`Pain level ${level} noted. I'll remember that.`);
    }
  };

  const handleAIQuery = async (query) => {
    setAiThinking(true);
    try {
      // Use the existing AI chat endpoint
      const res = await api.post("/chat", {
        message: query,
        context: "voice_assistant"
      });
      
      const aiResponse = res.data.response || res.data.message;
      
      // Truncate for speech (max ~200 words)
      let spokenResponse = aiResponse;
      if (aiResponse.length > 500) {
        spokenResponse = aiResponse.substring(0, 500) + "... Would you like me to continue?";
      }
      
      speak(spokenResponse);
    } catch (e) {
      speak("I'm having trouble connecting to the AI assistant. Please try again or ask a simpler question.");
    } finally {
      setAiThinking(false);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      setIsActive(false);
      wakeWordDetectedRef.current = false;
    } else {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
          setIsListening(true);
          toast.success('Voice assistant activated. Say "Hey Goodwin" to start.');
        } catch (e) {
          toast.error("Couldn't start voice recognition");
        }
      }
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating Voice Assistant Panel */}
      <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${isExpanded ? 'w-80' : 'w-auto'}`}>
        {/* Expanded Panel */}
        {isExpanded && (
          <Card className="mb-3 shadow-xl border-primary/20">
            <CardContent className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${isListening ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  <span className="font-medium text-sm">Goodwin Voice Assistant</span>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setShowSettings(true)}>
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setIsExpanded(false)}>
                    <Minimize2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Status */}
              <div className={`p-3 rounded-lg mb-3 ${isActive ? 'bg-green-50 border border-green-200' : 'bg-muted'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {isProcessing || aiThinking ? (
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  ) : isActive ? (
                    <Mic className="w-4 h-4 text-green-600 animate-pulse" />
                  ) : (
                    <MicOff className="w-4 h-4 text-muted-foreground" />
                  )}
                  <Badge variant={isActive ? "default" : "secondary"} className="text-xs">
                    {aiThinking ? "Thinking..." : isProcessing ? "Processing..." : isActive ? "Listening" : 'Say "Hey Goodwin"'}
                  </Badge>
                </div>
                {transcript && (
                  <p className="text-sm text-muted-foreground italic">"{transcript}"</p>
                )}
              </div>
              
              {/* Response */}
              {response && (
                <div className="p-3 bg-primary/5 rounded-lg mb-3 flex items-start gap-2">
                  <Volume2 className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                  <p className="text-sm">{response}</p>
                </div>
              )}
              
              {/* Quick Actions */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <Button size="sm" variant="outline" className="h-auto py-2 flex-col gap-1" onClick={() => executeAction("getMedications")}>
                  <Command className="w-4 h-4" />
                  <span className="text-xs">Meds</span>
                </Button>
                <Button size="sm" variant="outline" className="h-auto py-2 flex-col gap-1" onClick={() => executeAction("getStatus")}>
                  <Zap className="w-4 h-4" />
                  <span className="text-xs">Status</span>
                </Button>
                <Button size="sm" variant="outline" className="h-auto py-2 flex-col gap-1" onClick={() => executeAction("checkin")}>
                  <Sparkles className="w-4 h-4" />
                  <span className="text-xs">Check In</span>
                </Button>
                <Button size="sm" variant="outline" className="h-auto py-2 flex-col gap-1 text-red-600 border-red-200" onClick={() => executeAction("callNurse")}>
                  <Phone className="w-4 h-4" />
                  <span className="text-xs">Nurse</span>
                </Button>
              </div>
              
              {/* Conversation History */}
              {history.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                  {history.slice(-4).map((item, i) => (
                    <div key={i} className={`p-1.5 rounded ${item.type === 'user' ? 'bg-muted text-right' : 'bg-primary/5'}`}>
                      {item.text.substring(0, 60)}{item.text.length > 60 ? '...' : ''}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Main Button */}
        <div className="flex items-center gap-2 justify-end">
          {!isExpanded && isListening && (
            <Badge variant={isActive ? "default" : "secondary"} className="animate-pulse">
              {isActive ? "Listening..." : "Hey Goodwin"}
            </Badge>
          )}
          
          <Button
            size="lg"
            className={`rounded-full w-14 h-14 shadow-lg transition-all ${
              isListening ? 'bg-green-500 hover:bg-green-600' : ''
            } ${isActive ? 'ring-4 ring-green-300 animate-pulse' : ''}`}
            onClick={isExpanded ? toggleListening : () => setIsExpanded(true)}
            data-testid="premium-voice-btn"
          >
            {isListening ? (
              <Mic className="w-6 h-6" />
            ) : (
              <MicOff className="w-6 h-6" />
            )}
          </Button>
          
          {isExpanded && (
            <Button
              size="icon"
              variant="outline"
              className="rounded-full"
              onClick={toggleListening}
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              Voice Assistant Settings
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="commands" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="commands">Commands</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="languages">Languages</TabsTrigger>
            </TabsList>
            
            <TabsContent value="commands" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Say <strong>"Hey Goodwin"</strong> followed by any command:
              </p>
              
              <div className="space-y-3">
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Navigation
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• "Go to dashboard" / "My dashboard"</li>
                    <li>• "Open medications" / "My meds"</li>
                    <li>• "Show calendar" / "Appointments"</li>
                    <li>• "Open journal" / "My journal"</li>
                    <li>• "Ask AI" / "Chat"</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Actions
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• "Check in" - Start daily wellness check</li>
                    <li>• "Voice check in" - Hands-free check-in</li>
                    <li>• "Pain level 5" - Log your pain</li>
                    <li>• "Call nurse" - Call hospice nurse line</li>
                    <li>• "New journal entry" - Write in journal</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4" /> Questions
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• "What are my medications today?"</li>
                    <li>• "How am I doing?" - Get health status</li>
                    <li>• "What's coming up?" - View appointments</li>
                    <li>• "Who is my care team?"</li>
                    <li>• Any question - AI will answer!</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="settings" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Conversation Mode</Label>
                    <p className="text-xs text-muted-foreground">Keep listening after each response</p>
                  </div>
                  <Switch checked={conversationMode} onCheckedChange={setConversationMode} />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Listen After Response</Label>
                    <p className="text-xs text-muted-foreground">Automatically start listening</p>
                  </div>
                  <Switch checked={autoListen} onCheckedChange={setAutoListen} />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Sound Effects</Label>
                    <p className="text-xs text-muted-foreground">Play sounds for actions</p>
                  </div>
                  <Switch checked={soundEffects} onCheckedChange={setSoundEffects} />
                </div>
                
                <div className="space-y-2">
                  <Label>Voice Speed: {voiceSpeed}x</Label>
                  <Slider
                    value={[voiceSpeed]}
                    onValueChange={([v]) => setVoiceSpeed(v)}
                    min={0.75}
                    max={1.25}
                    step={0.05}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="languages" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Voice recognition supports multiple languages. Select your preferred language from the globe icon in the navigation bar.
              </p>
              
              <div className="space-y-2">
                <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <span>🇺🇸 English</span>
                  <Badge variant={i18n.language === 'en' ? 'default' : 'outline'}>
                    {i18n.language === 'en' ? 'Active' : 'Available'}
                  </Badge>
                </div>
                <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <span>🇪🇸 Español</span>
                  <Badge variant={i18n.language === 'es' ? 'default' : 'outline'}>
                    {i18n.language === 'es' ? 'Active' : 'Available'}
                  </Badge>
                </div>
                <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <span>🇨🇳 中文</span>
                  <Badge variant={i18n.language === 'zh' ? 'default' : 'outline'}>
                    {i18n.language === 'zh' ? 'Active' : 'Available'}
                  </Badge>
                </div>
                <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <span>🇻🇳 Tiếng Việt</span>
                  <Badge variant={i18n.language === 'vi' ? 'default' : 'outline'}>
                    {i18n.language === 'vi' ? 'Active' : 'Available'}
                  </Badge>
                </div>
                <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
                  <span>🇰🇷 한국어</span>
                  <Badge variant={i18n.language === 'ko' ? 'default' : 'outline'}>
                    {i18n.language === 'ko' ? 'Active' : 'Available'}
                  </Badge>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
